import { buttonFunctionTitle } from "./GlasButtonFrontView";
import { itx } from "../../../src/translationItx/itx";
import React, { useId, useState } from "react";
import type { DisplayFunction, GlassRockerDisplaySvgProps, RockerRowConfig } from "./GlasButtonFrontView";
import { ButtonRowModeControl } from "./ButtonRowModeControl";
import { ButtonSelectionOverlay } from "./ButtonSelectionOverlay";
import { fitSvgLabel } from "../../../src/components/fitSvgLabel";
import { buttonContentBox } from "./buttonContentBox";
import type { C1_ButtonColor, C1_ButtonOrientation } from "../types/C1_EloTypes";

export interface MechanicalPushButtonSvgProps extends GlassRockerDisplaySvgProps {
  className?: string;
  style?: React.CSSProperties;
  frameColor?: string;
  insertColor?: string;
  accentColor?: string;
  color?: C1_ButtonColor;
  orientation?: C1_ButtonOrientation;
}

/** Mechanische Darstellung mit derselben API wie der Glas-Taster. */
export function MechanicalPushButtonSvg({
  rows,
  selected = false,
  width = 92,
  height = 92,
  onButtonClick,
  getButtonChannelId,
  selectedButtonChannel,
  displayedButtonChannelIds,
  onNameChange,
  frameColor = "#eeeeec",
  insertColor = "#f7f7f5",
  accentColor = "#d8d8d4",
  className,
  style,
  onRowModeChange,
  usePhysicalSize = true,
  x,
  y,
  contentRotation = 0,
  contentReflected = false,
  color = "white",
  orientation = "vertical",
  constrainLabels = false,
}: MechanicalPushButtonSvgProps) {
  const uid = useId().replace(/:/g, "");
  const isBlack = color === "black";
  const buttonTextColor = isBlack ? "#f5f5f4" : "#626260";
  const [editingName, setEditingName] = useState<string | null>(null);
  const designWidth = 220;
  const designHeight = designWidth * (height / width);
  const outer = 7;
  const frame = 18;
  const innerX = outer + frame;
  const innerY = outer + frame;
  const innerWidth = designWidth - 2 * innerX;
  const innerHeight = designHeight - 2 * innerY;
  const gap = 6;
  const orientationRotation = orientation === "vertical" ? -90 : 0;
  const contentTransform = `translate(${designWidth / 2} ${designHeight / 2}) scale(${contentReflected ? -1 : 1} 1) rotate(${orientationRotation - contentRotation}) translate(${-designWidth / 2} ${-designHeight / 2})`;
  const rowHeight = (innerHeight - Math.max(0, rows.length - 1) * gap) / Math.max(1, rows.length);

  const renderButton = (
    row: RockerRowConfig,
    rowIndex: number,
    side: "left" | "right",
    target: "shared" | "left" | "right",
    x: number,
    y: number,
    buttonWidth: number,
  ) => {
    const fn = target === "shared" ? row.shared : row[target];
    const active = !!fn?.active;
    const editKey = `${rowIndex}-${target}`;
    const channelId = getButtonChannelId?.(rowIndex, side);
    const canEditName = !!onNameChange && !!channelId
      && (selectedButtonChannel?.id === channelId || !!displayedButtonChannelIds?.has(channelId));
    const isEditing = canEditName && editingName === editKey;
    const label = buttonContentBox(x, y, buttonWidth, rowHeight, orientation);
    const fontSize = Math.min(11, label.width * 0.12, label.height * 0.2);
    const title = fn?.name?.trim() || (fn ? buttonFunctionTitle(fn.channelType) : "");
    const fitted = constrainLabels
      ? fitSvgLabel(title, label.width - 10, fontSize)
      : undefined;
    const editHeight = 22;

    return (
      <g key={`${rowIndex}-${side}`} data-connection-control-region="">
        <rect
          data-connection-object-id={getButtonChannelId?.(rowIndex, side)}
          data-connection-exit-x={side === "right" ? 1 : -1}
          x={x}
          y={y}
          width={buttonWidth}
          height={rowHeight}
          rx={4}
          fill={`url(#${uid}-${active ? "pressed" : "button"})`}
          stroke={isBlack ? "#525252" : "#b7b7b4"}
          strokeWidth=".8"
          filter={`url(#${uid}-shadow)`}
          onClick={(event) => {
            const clickedSide =
              target === "shared"
                ? event.clientX <
                  event.currentTarget.getBoundingClientRect().left +
                    event.currentTarget.getBoundingClientRect().width / 2
                  ? "left"
                  : "right"
                : side;
            onButtonClick?.(rowIndex, clickedSide);
          }}
          style={{ cursor: onButtonClick ? "default" : "default" }}
        />
        <ButtonSelectionOverlay
          selected={(!!selectedButtonChannel && selectedButtonChannel.id === getButtonChannelId?.(rowIndex, side))
            || !!displayedButtonChannelIds?.has(getButtonChannelId?.(rowIndex, side) ?? "")}
          x={x}
          y={y}
          width={buttonWidth}
          height={rowHeight}
        />
        <path
          d={`M ${x + 4} ${y + 4} H ${x + buttonWidth - 4}`}
          stroke="#fff"
          strokeOpacity={active ? 0.35 : 0.88}
          pointerEvents="none"
        />
        <g transform={label.transform}>
          {fn && !isEditing && (
            <text
              x={label.x + label.width / 2}
              y={label.y + label.height / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fontFamily="Arial, sans-serif"
              fontSize={fontSize}
              textLength={fitted?.text ? fitted.width : undefined}
              lengthAdjust={fitted ? "spacingAndGlyphs" : undefined}
              fill={buttonTextColor}
              onClick={(event) => {
                event.stopPropagation();
                if (canEditName) setEditingName(editKey);
                else onButtonClick?.(rowIndex, target === "right" ? "right" : "left");
              }}
              style={{ cursor: canEditName ? "text" : "default" }}
            >
              {fitted ? fitted.text : title}
            </text>
          )}
          {fn && isEditing && (
            <foreignObject x={label.x + 5} y={label.y + (label.height - editHeight) / 2}
              width={Math.max(1, label.width - 10)} height={editHeight}>
              <input
                autoFocus
                aria-label={itx("c1.ui.Tastenname", "Tastenname")}
                value={fn.name ?? ""}
                placeholder={buttonFunctionTitle(fn.channelType)}
                onChange={(event) => onNameChange?.(rowIndex, target, event.target.value)}
                onClick={(event) => event.stopPropagation()}
                onFocus={(event) => event.currentTarget.select()}
                onBlur={() => setEditingName(null)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === "Escape") {
                    event.currentTarget.blur();
                  }
                }}
                style={{ width: "100%", height: "100%", boxSizing: "border-box", textAlign: "center", fontSize: "var(--dock-text-size, 14px)" }}
              />
            </foreignObject>
          )}
        </g>
      </g>
    );
  };

  const rowElements = rows.map((row, rowIndex) => {
    const y = innerY + rowIndex * (rowHeight + gap);
    if (row.mode === "shared") {
      return (
        <React.Fragment key={rowIndex}>
          {renderButton(row, rowIndex, "left", "shared", innerX, y, innerWidth)}
          <ButtonRowModeControl
            x={designWidth / 2}
            y={y + rowHeight - 7}
            isSplit={false}
            rowIndex={rowIndex}
            onToggle={onRowModeChange ? () => onRowModeChange(rowIndex, "split") : undefined}
          />
        </React.Fragment>
      );
    }
    const buttonWidth = (innerWidth - gap) / 2;
    return (
      <React.Fragment key={rowIndex}>
        {renderButton(row, rowIndex, "left", "left", innerX, y, buttonWidth)}
        {renderButton(row, rowIndex, "right", "right", innerX + buttonWidth + gap, y, buttonWidth)}
        <ButtonRowModeControl
          x={designWidth / 2}
          y={y + rowHeight - 7}
          isSplit
          rowIndex={rowIndex}
          onToggle={onRowModeChange ? () => onRowModeChange(rowIndex, "shared") : undefined}
        />
      </React.Fragment>
    );
  });

  return (
    <svg
      viewBox={`0 0 ${designWidth} ${designHeight}`}
      x={x}
      y={y}
      width={usePhysicalSize ? `${width}mm` : width}
      height={usePhysicalSize ? `${height}mm` : height}
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      className={className}
      style={{ maxWidth: "100%", height: "auto", ...style }}
    >
      <defs>
        <linearGradient id={`${uid}-frame`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={isBlack ? "#3f3f46" : "#fff"} />
          <stop offset="45%" stopColor={frameColor} />
          <stop offset="100%" stopColor={isBlack ? "#09090b" : "#c8c8c4"} />
        </linearGradient>
        <linearGradient id={`${uid}-insert`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={isBlack ? "#353535" : "#fff"} />
          <stop offset="60%" stopColor={insertColor} />
          <stop offset="100%" stopColor={accentColor} />
        </linearGradient>
        <linearGradient
          id={`${uid}-button`}
          x1="0"
          y1="0"
          x2="0"
          y2="1"
          gradientTransform={orientation === "vertical" ? "rotate(90 .5 .5)" : undefined}
        >
          <stop offset="0%" stopColor={isBlack ? "#404040" : "#fff"} />
          <stop offset="55%" stopColor={isBlack ? "#262626" : "#f4f4f1"} />
          <stop offset="100%" stopColor={isBlack ? "#171717" : "#d6d6d2"} />
        </linearGradient>
        <linearGradient
          id={`${uid}-pressed`}
          x1="0"
          y1="0"
          x2="0"
          y2="1"
          gradientTransform={orientation === "vertical" ? "rotate(90 .5 .5)" : undefined}
        >
          <stop offset="0%" stopColor="#d0d0cc" />
          <stop offset="100%" stopColor="#f8f8f5" />
        </linearGradient>
        <filter id={`${uid}-shadow`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1.4" stdDeviation="1.3" floodOpacity=".19" />
        </filter>
      </defs>
      <rect
        x={outer}
        y={outer}
        width={designWidth - 2 * outer}
        height={designHeight - 2 * outer}
        rx="7"
        fill={`url(#${uid}-frame)`}
        stroke={isBlack ? "#525252" : "#b7b7b2"}
        filter={`url(#${uid}-shadow)`}
      />
      <rect
        x={innerX - 3}
        y={innerY - 3}
        width={innerWidth + 6}
        height={innerHeight + 6}
        rx="5"
        fill={`url(#${uid}-insert)`}
        stroke={isBlack ? "#404040" : "#c3c3bf"}
      />
      <g transform={contentTransform}>{rowElements}</g>
      {selected && (
        <rect
          data-button-device-selection="true"
          x={outer}
          y={outer}
          width={designWidth - outer * 2}
          height={designHeight - outer * 2}
          rx={7}
          fill="#ffbe73"
          fillOpacity={0.3}
          pointerEvents="none"
        />
      )}
    </svg>
  );
}

export default MechanicalPushButtonSvg;
